import { arrayUtils, sectionsUtils } from '#packages/util';
import {
  isPopupContainer,
  isStripColumnContainer,
} from '#packages/documentServices';
import {
  getDockingFromFullWidthValue,
  getFullWidthValueFromDocking,
} from './fullWidthWithDocking';
import { getFullWidthValueFromPopupContainerProperties } from './fullWidthPopupContainerProperties';
import {
  getStripPropertiesFromFullWidthValue,
  getFullWidthValueFromStripProperties,
} from './fullWidthStripProperties';
import type { EditorAPI } from '#packages/editorAPI';
import type { CompRef, Docking, Rect } from 'types/documentServices';
import type { LayoutFullWidthSiteMargin, LayoutFullWidthValue } from '../type';
import type { LayoutGetApi } from '../layoutGetApi';

export function createLayoutFullWidthApi({
  editorAPI,
  layoutGetApi,
}: {
  editorAPI: EditorAPI;
  layoutGetApi: LayoutGetApi;
}) {
  function getFullWidth_Docking(compRef: CompRef): LayoutFullWidthValue {
    // eslint-disable-next-line @wix/santa-editor/deprecatedDSApi
    const compDocking = editorAPI.dsRead.components.layout.getDock(compRef);
    return getFullWidthValueFromDocking(compDocking);
  }

  function getFullWidth_Strip(compRef: CompRef): LayoutFullWidthValue {
    const compProperties = editorAPI.components.properties.get(compRef);

    return getFullWidthValueFromStripProperties(compProperties);
  }

  function getFullWidth_Popup(compRef: CompRef): LayoutFullWidthValue {
    const compProperties = editorAPI.components.properties.get(compRef);

    return getFullWidthValueFromPopupContainerProperties(compProperties);
  }

  function getFullWidth(compRef: CompRef): LayoutFullWidthValue {
    if (isStripColumnContainer(editorAPI.dsRead, compRef)) {
      return getFullWidth_Strip(compRef);
    }

    if (isPopupContainer(editorAPI.dsRead, compRef)) {
      return getFullWidth_Popup(compRef);
    }

    return getFullWidth_Docking(compRef);
  }

  function isFullWidth(compRefOrRefs: CompRef | CompRef[]) {
    return arrayUtils
      .asArray(compRefOrRefs)
      .every((compRef) => getFullWidth(compRef).isFullWidth);
  }

  /**
   * @deprecated use `isFullWidth` instead
   */
  function isHorizontallyStretchedToScreen(compRefOrRefs: CompRef | CompRef[]) {
    return arrayUtils.asArray(compRefOrRefs).every((compRef: CompRef) =>
      // DS source https://github.com/wix-private/santa-core/blob/f1a5a0d8e67d5d606cd1983a47a56794e4f12238/santa-core-utils/src/coreUtils/core/dockUtils.js#L62-L68
      editorAPI.dsRead.components.layout.isHorizontallyStretchedToScreen(
        compRef,
      ),
    );
  }

  function reparentComponent(compRef: CompRef, containerRefTarget: CompRef) {
    const [compRefUpdated] = arrayUtils.asArray(
      editorAPI.components.setContainer(compRef, containerRefTarget),
    );
    const compRectUpdated = layoutGetApi.get_rect(compRefUpdated);

    return {
      compRefUpdated,
      compRectUpdated,
    };
  }

  function isComponentInStretchableContainer(compRef: CompRef) {
    return editorAPI.components.is.potentialContainerForScreenWidthComp(
      editorAPI.components.getContainer(compRef),
    );
  }

  function getComponentStreatchableContainer(compRef: CompRef) {
    return sectionsUtils.isSectionsEnabled()
      ? editorAPI.sections.getClosestSectionLike(compRef)
      : editorAPI.pages.getFocusedPage();
  }

  interface StretchComponentOptions {
    siteMargin?: LayoutFullWidthSiteMargin;
    docking?: Docking;
  }

  function areSiteMarginsEqual(
    siteMarginA: LayoutFullWidthSiteMargin,
    siteMarginB: LayoutFullWidthSiteMargin,
  ) {
    if (siteMarginA === siteMarginB) {
      // same object reference or both are undefined
      return true;
    }

    if (!siteMarginA || !siteMarginB) {
      // one of them is undefined
      return false;
    }

    return (
      siteMarginA.value === siteMarginB.value &&
      siteMarginA.type === siteMarginB.type
    );
  }

  const SITE_MARGIN_DEFAULT: LayoutFullWidthSiteMargin = {
    value: 0,
    type: 'px',
  };

  function stretchComponent_Docking(
    compRef: CompRef,
    options?: StretchComponentOptions,
  ) {
    const fullWidthPrev = getFullWidth_Docking(compRef);
    const siteMargin =
      options?.siteMargin ?? fullWidthPrev.siteMargin ?? SITE_MARGIN_DEFAULT;

    if (
      !options?.docking &&
      fullWidthPrev.isFullWidth &&
      areSiteMarginsEqual(siteMargin, fullWidthPrev.siteMargin)
    ) {
      return;
    }

    if (!isComponentInStretchableContainer(compRef)) {
      ({ compRefUpdated: compRef } = reparentComponent(
        compRef,
        getComponentStreatchableContainer(compRef),
      ));
    }
    const docking =
      options?.docking ??
      getDockingFromFullWidthValue({
        isFullWidth: true,
        siteMargin,
      });

    // eslint-disable-next-line @wix/santa-editor/deprecatedDSApi
    editorAPI.components.layout.setDock(compRef, docking);

    if (!fullWidthPrev.isFullWidth) {
      editorAPI.history.add('stretchComponent');
    } else {
      editorAPI.history.debouncedAdd('stretchComponent - update siteMargin');
    }
  }

  function stretchComponent_Strip(
    compRef: CompRef,
    options?: StretchComponentOptions,
  ) {
    const fullWidthPrev = getFullWidth_Strip(compRef);
    const siteMargin =
      options?.siteMargin ?? fullWidthPrev.siteMargin ?? SITE_MARGIN_DEFAULT;

    if (
      fullWidthPrev.isFullWidth &&
      areSiteMarginsEqual(siteMargin, fullWidthPrev.siteMargin)
    ) {
      return;
    }

    editorAPI.components.properties.update(
      compRef,
      getStripPropertiesFromFullWidthValue({
        isFullWidth: true,
        siteMargin,
      }),
      {
        dontAddToUndoRedoStack: true,
      },
    );

    if (!fullWidthPrev.isFullWidth) {
      editorAPI.history.add('stretchComponent');
    } else {
      editorAPI.history.debouncedAdd('stretchComponent - update siteMargin');
    }
  }

  async function stretchComponent(
    compRefOrRefs: CompRef | CompRef[],
    options?: StretchComponentOptions,
  ) {
    const compRef = arrayUtils.asSingleThrowIfMultiple(compRefOrRefs);
    if (isStripColumnContainer(editorAPI.dsRead, compRef)) {
      return stretchComponent_Strip(compRef, options);
    }

    return stretchComponent_Docking(compRef, options);
  }

  interface UnStretchComponentOptions {
    // containerRef, usually is a previous component container before stretch
    containerRef?: CompRef;
    compRect?: Partial<Rect>;
  }

  function unStretchComponent_Docking(
    compRef: CompRef,
    options?: UnStretchComponentOptions,
  ) {
    // eslint-disable-next-line @wix/santa-editor/deprecatedDSApi
    editorAPI.components.layout.unDock(compRef);

    editorAPI.dsActions.waitForChangesApplied(() => {
      // eslint-disable-next-line @wix/santa-editor/deprecatedDSApi
      editorAPI.components.layout.update(
        compRef,
        {
          ...options?.compRect,
          width: options?.compRect?.width ?? editorAPI.site.getWidth(),
        },
        true,
      );

      const containerRefCurrent = editorAPI.components.getContainer(compRef);
      if (
        options?.containerRef &&
        !editorAPI.utils.isSameRef(options?.containerRef, containerRefCurrent)
      ) {
        reparentComponent(compRef, options?.containerRef);
      }
    });

    editorAPI.history.add('unStretchComponent');
  }

  function unStretchComponent_Strip(compRef: CompRef) {
    editorAPI.components.properties.update(
      compRef,
      getStripPropertiesFromFullWidthValue({
        isFullWidth: false,
      }),
      {
        dontAddToUndoRedoStack: true,
      },
    );

    editorAPI.history.add('unStretchComponent');
  }

  async function unStretchComponent(
    compRefOrRefs: CompRef | CompRef[],
    options?: UnStretchComponentOptions,
  ) {
    const compRef = arrayUtils.asSingleThrowIfMultiple(compRefOrRefs);

    if (isStripColumnContainer(editorAPI.dsRead, compRef)) {
      return unStretchComponent_Strip(compRef);
    }

    return unStretchComponent_Docking(compRef, options);
  }

  return {
    getFullWidth,
    isFullWidth,
    isHorizontallyStretchedToScreen,
    stretchComponent,
    unStretchComponent,
  };
}

export type LayoutFullWidthApi = ReturnType<typeof createLayoutFullWidthApi>;
